import React, { JSX, startTransition, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Container } from '@mui/material';
import { ROUTES } from '../../common/constants/routes';
import MainNavigation from '../../common/main-navigation/MainNavigation';
import { useAuth } from '@clerk/clerk-react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import { LOCATION_EXCEPTIONS } from '../../common/constants/location_exceptions';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { RootState } from '../../store';
import MessageModal from '../../common/message-modal/MessageModal';
import UploadProgress from '../../common/upload-progress/UploadProgress';
import { useTranslation } from 'react-i18next';
import { hideMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import { selectTaskIds } from '../../slices/tasks/tasksSlice';
import { clearPlaceholdersIndexes } from '../../slices/template-placeholders-indexes/templatePlaceholdersIndexes';
import { clearPlaceholdersIds } from '../../slices/template-placeholders-ids/templatePlaceholdersIds';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';

type UpdatePlanMessageContentProps = {
  messageText?: string | null;
};

const UpdatePlanMessageContent = ({ messageText }: UpdatePlanMessageContentProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div>
      <p>{messageText || t('messages.monthLimitReached')}</p>
      <p>
        {t('messages.contactWording')}{' '}
        <a id='layoutSupportBusinesskitzComAuLink' href='mailto:support@businesskitz.com.au'>
          support@businesskitz.com.au
        </a>
        .
      </p>
    </div>
  );
};

const Layout = (): JSX.Element => {
  const { documentId } = useParams();
  const { id } = useParams();
  const [pendingStatus, setPendingStatus] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const taskIds = useAppSelector(selectTaskIds);
  const company = useAppSelector(selectAllCompanies)[0];

  const showProgressOverlay = useAppSelector(
    (state: RootState) => state.showProgressOverlay.showOverlay
  );

  const showPlanUpgradingMessage = useAppSelector(
    (state: RootState) => state.showPlanUpgradingMessage.showMessage
  );
  const planUpgradingMessageText = useAppSelector(
    (state: RootState) => state.showPlanUpgradingMessage.messageText
  );
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  const locationIncludeExceptionUrl = LOCATION_EXCEPTIONS.some((el) => {
    return location.pathname.includes(el);
  });
  const locationWithFullWidth = [
    ROUTES.SIGNING,
    ROUTES.DOCUMENTS,
    ROUTES.DOCUMENT_VAULT_CATEGORY_DOCUMENT,
  ];
  const locationWithFullWidthUrl = locationWithFullWidth.some((el) => {
    return location.pathname.includes(el);
  });

  const tokenLoaded = isSignedIn && isLoaded;

  useEffect(() => {
    if (locationIncludeExceptionUrl) {
      startTransition(() => setPendingStatus(false));
    } else if (!locationIncludeExceptionUrl && tokenLoaded) {
      startTransition(() => setPendingStatus(false));
    }
  }, [locationIncludeExceptionUrl, tokenLoaded]);

  useEffect(() => {
    if (location.pathname === ROUTES.INDEX) {
      navigate(ROUTES.HOME);
    }
    dispatch(clearPlaceholdersIds());
    dispatch(clearPlaceholdersIndexes());
  }, [location.pathname, navigate, dispatch]);

  const renderedLayout = () => {
    return (
      <React.Fragment>
        {pendingStatus ? (
          <ProgressOverlay />
        ) : (
          <Box
            className={
              'layout updated-variant' +
              (location.pathname === ROUTES.PRICING ? ' pricing-page' : '') +
              (location.pathname === ROUTES.HOME ? ' home-page' : '')
            }
          >
            <MainNavigation />
            <Box className='layout-content'>
              <Container
                className={location.pathname === ROUTES.HOME ? 'home-page-container' : ''}
                maxWidth={locationWithFullWidthUrl || documentId || id ? false : 'lg'}
              >
                <Outlet />
                {taskIds && taskIds.length > 0 && <UploadProgress taskIds={taskIds} />}
              </Container>
              {showPlanUpgradingMessage && (
                <MessageModal
                  open={showPlanUpgradingMessage}
                  modalContent={<UpdatePlanMessageContent messageText={planUpgradingMessageText} />}
                  actionButtonText={t('buttons.ok')}
                  actionButtonFn={() => {
                    if (!company?.stripe_product_id) {
                      navigate(ROUTES.PRICING);
                    }
                    dispatch(hideMessage());
                  }}
                  goBackButtonText={t('buttons.later')}
                  onClose={() => {
                    dispatch(hideMessage());
                  }}
                />
              )}
              {showProgressOverlay && <ProgressOverlay />}
            </Box>
          </Box>
        )}
      </React.Fragment>
    );
  };

  if (!tokenLoaded) return <ProgressOverlay />;

  return renderedLayout();
};
export default Layout;
